import { formatDuration, formatUKForDisplay, getCounterpartyNumber } from '@marnic/shared';
import { relativeTime } from '../lib/utils';
import type { CallRow } from './useRecentCalls';

interface Props {
  items: CallRow[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  loading: boolean;
  error: boolean;
}

export function CallList({ items, selectedId, onSelect, loading, error }: Props): JSX.Element {
  if (loading) return <div className="p-6 text-center text-sm text-ink-500">Loading…</div>;
  if (error) return <div className="p-6 text-center text-sm text-red-600">Failed to load calls.</div>;
  if (items.length === 0) return <div className="p-6 text-center text-sm text-ink-500">No calls.</div>;

  return (
    <ul className="divide-y divide-ink-100">
      {items.map((c) => {
        const selected = c.id === selectedId;
        const counterparty = formatUKForDisplay(getCounterpartyNumber(c));
        const name = c.lead
          ? `${c.lead.firstName} ${c.lead.lastName}${c.lead.company ? ` · ${c.lead.company}` : ''}`
          : null;
        const isVoipline = (c as { provider?: string }).provider === 'VOIPLINE';
        const directionTone =
          c.direction === 'inbound' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700';

        return (
          <li key={c.id}>
            <button
              type="button"
              onClick={() => onSelect(c.id)}
              className={`w-full text-left px-4 py-3 transition hover:bg-ink-50 ${selected ? 'bg-brand-50' : ''}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium truncate">{name ?? counterparty}</div>
                  {name && (
                    <div className="flex items-center gap-1.5 text-xs font-mono text-ink-500">
                      <span className="truncate">{counterparty}</span>
                      {isVoipline && (
                        <span className="shrink-0 rounded bg-purple-100 px-1.5 py-0.5 text-[9px] font-sans font-semibold uppercase tracking-wider text-purple-700">
                          VP
                        </span>
                      )}
                    </div>
                  )}
                  {!name && isVoipline && (
                    <div>
                      <span className="rounded bg-purple-100 px-1.5 py-0.5 text-[9px] font-sans font-semibold uppercase tracking-wider text-purple-700">
                        VP
                      </span>
                    </div>
                  )}
                </div>
                <span
                  className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${directionTone}`}
                >
                  {c.direction}
                </span>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-ink-500">
                <span>{relativeTime(c.startedAt)}</span>
                <span className="font-mono">{formatDuration(c.durationSeconds ?? 0)}</span>
                <span className="capitalize">{c.disposition ?? c.status}</span>
                {c.recordingUrl && (
                  <span className="text-emerald-600" title="Has recording" aria-label="Has recording">
                    ●
                  </span>
                )}
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
